'use client'
import { isFilled } from "@prismicio/client";
import { PrismicNextLink } from "@prismicio/next";
import { PrismicRichText, PrismicImage } from "@prismicio/react";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/react-splide/css/core';

/**
 * @typedef {import("@prismicio/client").Content.RelatedContentGridSlice} RelatedContentGridSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<RelatedContentGridSlice>} RelatedContentGridProps
 * @type {import("react").FC<RelatedContentGridProps>}
 */

const Card = ({ item, idx }) => (
  <div
    key={idx}
    className="flex flex-col h-full"
  >
    <div className="h-60 bg-light-green">
      {isFilled.image(item.article.data.preview_image) && (
        <PrismicImage
          field={item.article.data.preview_image}
          className="w-full h-full object-cover min-h-60"
        />
      )}
    </div>

    <div className="p-8 pb-11 bg-light-gray h-full flex flex-col justify-between gap-4">
      <div>
        {isFilled.date(item.article.data.preview_date) && (
          <span className="text-date">
            {new Date(item.article.data.preview_date).toLocaleDateString("fr-FR")}
          </span>
        )}
        {isFilled.richText(item.article.data.preview_title) && (
          <PrismicRichText
            field={item.article.data.preview_title}
            components={{
              heading2: ({ children }) => (
                <h3 className="h3-sm">{children}</h3>
              ),
              heading3: ({ children }) => (
                <h3 className="h3-sm">{children}</h3>
              ),
            }}
          />
        )}
      </div>

      <div className="flex justify-between items-end">
        {item.article.tags.length > 0 && (
          <span className="text-tags tag-light-green text-white">
            {item.article.tags[0]}
          </span>
        )}
        <div className="flex-1 text-end">
          <PrismicNextLink
            document={item.article}
            className="p-sm inline-block py-1"
          >
            Lire plus
          </PrismicNextLink>
        </div>
      </div>
    </div>
  </div>

);

const CardGuide = ({ item, idx }) => (
  <div key={idx} className={`w-full h-full`}>
    <PrismicNextLink
      field={item.guide}
      className="h-full flex flex-col gap-6 justify-between no-underline p-5 md:p-7 text-white min-h-70 md:min-h-90 relative bg-cover bg-no-repeat bg-center hover:scale-[1.02] transition-all"
      style={{
        backgroundImage: `linear-gradient(#003A33B2, #003A33B2), url(${item.guide.data.preview_image.url})`,
      }}
    >
      {isFilled.richText(item.guide.data.preview_title) && (
        <PrismicRichText
          field={item.guide.data.preview_title}
          components={{
            heading2: ({ children }) => <h3 className="title-guide">{children}</h3>,
            heading3: ({ children }) => <h3 className="title-guide">{children}</h3>,
          }}
        />
      )}

      {item.guide.tags.length > 0 && (
        <div>
          <span className="text-tags tag-yellow text-dark-green">
            {item.guide.tags[0]}
          </span>
        </div>
      )}
    </PrismicNextLink>
  </div>
);

const RelatedContentGrid = ({ slice }) => {
  const { title } = slice.primary;

  // Sélectionne le bon groupe selon la variation
  const items = slice.variation === 'guide'
    ? slice.primary.guides
    : slice.primary.articles;

  const CardComponent = slice.variation === 'guide' ? CardGuide : Card;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="framing mt-16 mb-20 md:mt-30 md:mb-40 text-dark-green"
    >
      <div className="flex flex-col gap-10">
        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading2: ({ children }) => (
                <h2 className="h2">{children}</h2>
              ),
            }}
          />
        )}

        {isFilled.group(items) && (
          <div>
            <div className="grid-cols-3 gap-10 hidden lg:grid">
              {items.map((item, idx) => (
                <CardComponent item={item} idx={idx} key={idx} />
              ))}
            </div>
            <div className="lg:hidden">
              <Splide
                aria-label="Articles liés"
                options={{
                  type: "slide",
                  gap: "2.5rem",
                  perPage: 2,
                  pagination: true,
                  arrows: false,
                  drag: true,
                  snap: true,
                  padding: { left: "0rem", right: "0rem" },
                  breakpoints: {
                    1023: { perPage: 2, gap: "2rem", padding: { left: 0, right: 0 } },
                    640: { perPage: 1, gap: "1.25rem", padding: { left: 0, right: 0 } },
                  },
                }}
                className="overflow-visible"
              >
                {items.map((item, idx) => (
                  <SplideSlide key={idx}>
                    <CardComponent item={item} idx={idx} />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default RelatedContentGrid;
